import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Clock, Users, Briefcase, RefreshCw, AlertCircle, Search, Activity, Calendar, ChevronRight, ChevronDown } from 'lucide-react';
import { getCurrentUser, getAccessToken } from '../services/auth';

const DEFAULT_EMPLOYEES_PATH = '/v1/employees';
const DEFAULT_JOBCODES_PATH = '/v1/jobcodes';
const DEFAULT_HOURS_PATH = '/v1/hours';
const LS_EMP = 'tcp_path_employees';
const LS_JOB = 'tcp_path_jobcodes';
const LS_HOURS = 'tcp_path_hours';

const TH = { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)', position: 'sticky', top: 0, whiteSpace: 'nowrap' };
const TD = { padding: '8px 10px', fontSize: 13, borderBottom: '1px solid var(--gray-100)', color: 'var(--gray-800)', verticalAlign: 'top' };
const INPUT = { padding: '7px 12px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };
const BTN = (bg, disabled) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: disabled ? 'var(--gray-300)' : bg, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit' });
const CODE = { fontFamily: '"SF Mono", Menlo, Consolas, monospace', fontSize: 11.5, background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 4, padding: '8px 10px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'var(--gray-800)', maxHeight: 360, overflow: 'auto' };

async function tcpCall(body) {
  const token = getAccessToken();
  const res = await fetch('/.netlify/functions/tcp-proxy', {
    method: 'POST',
    // tcp-proxy requires a signed-in Admin/IT/Manager session (Oct 29, 2026).
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  try { return { httpStatus: res.status, ...JSON.parse(text) }; }
  catch (_) { return { httpStatus: res.status, ok: false, raw: text }; }
}

function extractList(data) {
  let arr = null;
  if (Array.isArray(data)) arr = data;
  else if (data && typeof data === 'object') {
    for (const key of ['data', 'items', 'results', 'employees', 'jobcodes', 'jobCodes', 'hours', 'records']) {
      if (Array.isArray(data[key])) { arr = data[key]; break; }
    }
    if (!arr) for (const k of Object.keys(data)) if (Array.isArray(data[k])) { arr = data[k]; break; }
  }
  return arr || [];
}

// Walk TCP pagination until a short page comes back. TCP caps perPage at 250;
// Employees and Job Codes can exceed 250, so pages are joined. Stops at 10
// pages (2500 rows) as a safety net.
async function fetchAllPages(path, baseQuery = {}) {
  const perPage = baseQuery.perPage || 250;
  const all = [];
  for (let page = 1; page <= 10; page++) {
    const r = await tcpCall({ path, method: 'GET', query: { ...baseQuery, page, perPage } });
    if (!r.ok) {
      return { data: all, error: { status: r.status || r.httpStatus, url: r.url, body: r.data || r.raw || r.error } };
    }
    const rows = extractList(r.data);
    all.push(...rows);
    if (rows.length < perPage) break;
  }
  return { data: all, error: null };
}

// /v1/hours returns [{employee: {...}, hours?: [...], segments?: [...], info?: "..."}]
// Flatten each nested time entry into its own row tagged with _employee.
// Employees with no entries (info-only) become a single row with _empty: true.
function flattenHours(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const first = rows[0];
  if (!first || typeof first !== 'object' || !first.employee) return rows;
  const entryKey = ['hours', 'segments', 'workSegments', 'timeEntries'].find(k => rows.some(r => Array.isArray(r[k])));
  const flat = [];
  rows.forEach(item => {
    const emp = item.employee;
    const entries = entryKey && Array.isArray(item[entryKey]) ? item[entryKey] : [];
    if (entries.length > 0) {
      entries.forEach(e => flat.push({ ...e, _employee: emp }));
    } else {
      flat.push({ _employee: emp, _empty: true, _info: item.info || 'No hours in range' });
    }
  });
  return flat;
}

function get(obj, ...keys) {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return undefined;
}

function parseAddress(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch (_) { return null; }
}

function fmtDate(s) {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(s).slice(0, 10);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtTime(s) {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(s);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function fmtYesNo(v) {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

function isoDate(d) { return d.toISOString().slice(0, 10); }

function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d; }

// TCP returns durations as "H:MM" (e.g. "3:43"). Convert to decimal hours.
function parseHMM(s) {
  if (typeof s !== 'string') return 0;
  const parts = s.split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (isNaN(h)) return 0;
  return h + (isNaN(m) ? 0 : m / 60);
}

// Sum regular + overtime buckets on a /v1/hours entry.
function entryHours(row) {
  const h = row && row.hours;
  if (typeof h === 'object' && h !== null) {
    return parseHMM(h.regular) + parseHMM(h.overtime1) + parseHMM(h.overtime2);
  }
  if (typeof h === 'number' && !isNaN(h)) return h;
  // Fallback: compute from timeIn/timeOut
  const inT = get(row, 'timeIn', 'clockInTime', 'inTime', 'startTime');
  const outT = get(row, 'timeOut', 'clockOutTime', 'outTime', 'endTime');
  if (inT && outT) {
    const ms = new Date(outT) - new Date(inT);
    if (!isNaN(ms) && ms > 0) return ms / 3600000;
  }
  return 0;
}

export default function TimeClock() {
  const user = getCurrentUser();
  const userRoles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map(r => r.trim());
  const isAllowed = userRoles.includes('Admin') || userRoles.includes('IT') || userRoles.includes('Manager');

  const [tab, setTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [jobCodes, setJobCodes] = useState([]);
  const [hours, setHours] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [jobLoading, setJobLoading] = useState(false);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [empError, setEmpError] = useState(null);
  const [jobError, setJobError] = useState(null);
  const [hoursError, setHoursError] = useState(null);
  const [empSearch, setEmpSearch] = useState('');
  const [jobSearch, setJobSearch] = useState('');

  const [hoursStart, setHoursStart] = useState(isoDate(daysAgo(7)));
  const [hoursEnd, setHoursEnd] = useState(isoDate(new Date()));
  const [hoursFetchedAt, setHoursFetchedAt] = useState(null);

  const empPath = (typeof window !== 'undefined' && localStorage.getItem(LS_EMP)) || DEFAULT_EMPLOYEES_PATH;
  const jobPath = (typeof window !== 'undefined' && localStorage.getItem(LS_JOB)) || DEFAULT_JOBCODES_PATH;
  const hoursPath = (typeof window !== 'undefined' && localStorage.getItem(LS_HOURS)) || DEFAULT_HOURS_PATH;

  const loadEmployees = useCallback(async () => {
    setEmpLoading(true); setEmpError(null);
    try {
      const rows = await fetchAllPages(empPath, { perPage: 250 });
      setEmployees(rows.data);
      if (rows.error) setEmpError(rows.error);
    } catch (e) { setEmpError({ status: 0, body: e.message }); }
    finally { setEmpLoading(false); }
  }, [empPath]);

  const loadJobCodes = useCallback(async () => {
    setJobLoading(true); setJobError(null);
    try {
      const rows = await fetchAllPages(jobPath, { perPage: 250 });
      setJobCodes(rows.data);
      if (rows.error) setJobError(rows.error);
    } catch (e) { setJobError({ status: 0, body: e.message }); }
    finally { setJobLoading(false); }
  }, [jobPath]);

  const loadHours = useCallback(async () => {
    setHoursLoading(true); setHoursError(null);
    try {
      const r = await tcpCall({
        path: hoursPath,
        method: 'GET',
        query: { startDate: hoursStart, stopDate: hoursEnd, perPage: 250 },
      });
      if (!r.ok) { setHoursError({ status: r.status || r.httpStatus, url: r.url, body: r.data || r.raw || r.error }); setHours([]); }
      else { setHours(flattenHours(extractList(r.data))); setHoursFetchedAt(new Date()); }
    } catch (e) { setHoursError({ status: 0, body: e.message }); }
    finally { setHoursLoading(false); }
  }, [hoursPath, hoursStart, hoursEnd]);

  useEffect(() => {
    if (!isAllowed) return;
    if (tab === 'employees' && employees.length === 0 && !empLoading && !empError) loadEmployees();
    if (tab === 'job-codes' && jobCodes.length === 0 && !jobLoading && !jobError) loadJobCodes();
    if (tab === 'hours') {
      // Ensure employees + jobCodes are loaded so we can resolve names
      if (employees.length === 0 && !empLoading && !empError) loadEmployees();
      if (jobCodes.length === 0 && !jobLoading && !jobError) loadJobCodes();
    }
  }, [tab, isAllowed, employees.length, jobCodes.length, empLoading, jobLoading, empError, jobError, loadEmployees, loadJobCodes]);

  // Lookup maps for the Hours tab
  const employeeById = useMemo(() => {
    const m = new Map();
    employees.forEach(e => {
      const id = get(e, 'employeeId', 'id', 'employeeID');
      if (id !== undefined) m.set(String(id), e);
    });
    return m;
  }, [employees]);

  const jobCodeById = useMemo(() => {
    const m = new Map();
    jobCodes.forEach(j => {
      const id = get(j, 'jobCodeID', 'jobcodeid', 'jobCodeId', 'id');
      if (id !== undefined) m.set(String(id), j);
    });
    return m;
  }, [jobCodes]);

  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(e => JSON.stringify(e).toLowerCase().includes(q));
  }, [employees, empSearch]);

  const filteredJobs = useMemo(() => {
    const q = jobSearch.trim().toLowerCase();
    if (!q) return jobCodes;
    return jobCodes.filter(j => JSON.stringify(j).toLowerCase().includes(q));
  }, [jobCodes, jobSearch]);

  if (!isAllowed) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>
        Access restricted. Time Clock requires Admin, IT, or Manager role.
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1600, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Clock size={22} color="var(--navy)" />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--navy)' }}>Time Clock</h1>
          <span style={{ fontSize: 11, padding: '2px 8px', background: 'var(--red)', color: '#fff', borderRadius: 10, fontWeight: 600, letterSpacing: '0.03em' }}>PoC</span>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>TCP Plus · namespace 328039</div>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--gray-200)', marginBottom: 20 }}>
        {[
          { id: 'employees', label: 'Employees', icon: Users, count: employees.length },
          { id: 'job-codes', label: 'Job Codes', icon: Briefcase, count: jobCodes.length },
          { id: 'hours', label: 'Hours', icon: Calendar, count: hours.length || null },
          { id: 'diagnostics', label: 'Diagnostics', icon: Activity },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '9px 16px', border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: 13, fontWeight: active ? 700 : 500, fontFamily: 'inherit',
              color: active ? 'var(--orange)' : 'var(--gray-600)',
              borderBottom: active ? '2px solid var(--orange)' : '2px solid transparent',
              marginBottom: -1,
            }}>
              <Icon size={14} />
              {t.label}
              {typeof t.count === 'number' && t.count > 0 && (
                <span style={{ background: 'var(--gray-200)', color: 'var(--gray-700)', fontSize: 10.5, padding: '1px 7px', borderRadius: 10, fontWeight: 600 }}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'employees' && (
        <EmployeesTab rows={filteredEmployees} total={employees.length} loading={empLoading} error={empError}
          search={empSearch} onSearch={setEmpSearch} onReload={loadEmployees} endpointPath={empPath} />
      )}
      {tab === 'job-codes' && (
        <JobCodesTab rows={filteredJobs} total={jobCodes.length} loading={jobLoading} error={jobError}
          search={jobSearch} onSearch={setJobSearch} onReload={loadJobCodes} endpointPath={jobPath} />
      )}
      {tab === 'hours' && (
        <HoursTab rows={hours} loading={hoursLoading} error={hoursError}
          startDate={hoursStart} endDate={hoursEnd} onStart={setHoursStart} onEnd={setHoursEnd}
          onFetch={loadHours} fetchedAt={hoursFetchedAt} endpointPath={hoursPath}
          employeeById={employeeById} jobCodeById={jobCodeById} />
      )}
      {tab === 'diagnostics' && (
        <DiagnosticsTab onEndpointSaved={() => { setEmployees([]); setJobCodes([]); setHours([]); setEmpError(null); setJobError(null); setHoursError(null); }} />
      )}
    </div>
  );
}

function Toolbar({ search, onSearch, onReload, loading, endpointPath, rowCount, total, placeholder, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
      {onSearch && (
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
          <input value={search} onChange={e => onSearch(e.target.value)} placeholder={placeholder}
            style={{ ...INPUT, width: '100%', paddingLeft: 30 }} />
        </div>
      )}
      {typeof total === 'number' && (
        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
          {loading ? 'Loading…' : rowCount === total ? `${total} total` : `${rowCount} of ${total}`}
        </div>
      )}
      {children}
      <div style={{ flex: 1 }} />
      <code style={{ fontSize: 11, color: 'var(--gray-400)', fontFamily: '"SF Mono", Menlo, Consolas, monospace' }}>{endpointPath}</code>
      <button onClick={onReload} disabled={loading} style={BTN('var(--navy)', loading)}>
        <RefreshCw size={13} className={loading ? 'spin' : ''} /> Reload
      </button>
    </div>
  );
}

function ErrorBanner({ error, endpointPath, hint }) {
  return (
    <div style={{ padding: 14, background: '#fff4f4', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: 6 }}>
        <AlertCircle size={14} /> Request failed (HTTP {error.status || '—'})
      </div>
      <div style={{ fontSize: 12.5, marginBottom: 6 }}>Endpoint: <code>{error.url || endpointPath}</code></div>
      {hint && <div style={{ fontSize: 12, marginBottom: 6, color: '#7f1d1d' }}>{hint}</div>}
      <pre style={{ ...CODE, background: '#fff', borderColor: '#fca5a5', color: '#7f1d1d', margin: 0 }}>
        {typeof error.body === 'string' ? error.body : JSON.stringify(error.body, null, 2)}
      </pre>
    </div>
  );
}

function DataTable({ rows, columns, emptyLabel, rowKey, expandable }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (k) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  if (rows.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', border: '1px dashed var(--gray-300)', borderRadius: 8 }}>
        No {emptyLabel} in result.
      </div>
    );
  }
  return (
    <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {expandable && <th style={{ ...TH, width: 28 }}></th>}
            {columns.map(c => <th key={c.key} style={{ ...TH, ...(c.thStyle || {}) }}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const k = rowKey ? rowKey(row, i) : i;
            const isOpen = expanded.has(k);
            return (
              <React.Fragment key={k}>
                <tr style={{ background: i % 2 ? 'var(--gray-50)' : '#fff' }}>
                  {expandable && (
                    <td style={{ ...TD, cursor: 'pointer', color: 'var(--gray-400)' }} onClick={() => toggle(k)}>
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                  )}
                  {columns.map(c => (
                    <td key={c.key} style={{ ...TD, ...(c.tdStyle || {}) }}>
                      {c.render ? c.render(row) : renderCell(row[c.key])}
                    </td>
                  ))}
                </tr>
                {expandable && isOpen && (
                  <tr style={{ background: '#fafbfc' }}>
                    <td colSpan={columns.length + 1} style={{ padding: '10px 14px' }}>
                      <pre style={{ ...CODE, margin: 0, maxHeight: 240 }}>{JSON.stringify(row, null, 2)}</pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function renderCell(v) {
  if (v === null || v === undefined || v === '') return <span style={{ color: 'var(--gray-400)' }}>—</span>;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return <code style={{ fontSize: 11 }}>{JSON.stringify(v).slice(0, 60)}</code>;
  const s = String(v);
  return s.length > 80 ? s.slice(0, 78) + '…' : s;
}

function EmployeesTab({ rows, total, loading, error, search, onSearch, onReload, endpointPath }) {
  const columns = [
    { key: 'id', label: 'ID', render: r => get(r, 'employeeId', 'id', 'employeeID') ?? '—', tdStyle: { fontFamily: '"SF Mono", Menlo, Consolas, monospace', fontSize: 12, color: 'var(--gray-600)' } },
    { key: 'name', label: 'Name', render: r => {
        const first = get(r, 'firstName', 'first_name');
        const last = get(r, 'lastName', 'last_name');
        const full = [first, last].filter(Boolean).join(' ');
        return full || get(r, 'employeeName', 'name') || '—';
      } },
    { key: 'email', label: 'Email', render: r => {
        const email = get(r, 'email', 'emailAddress');
        return email ? <a href={`mailto:${email}`} style={{ color: 'var(--navy)', textDecoration: 'none' }}>{email}</a> : '—';
      } },
    { key: 'hired', label: 'Hired', render: r => fmtDate(get(r, 'hireDate', 'hire_date', 'hiredate')) },
    { key: 'location', label: 'Location', render: r => {
        const addr = parseAddress(get(r, 'address', 'homeAddress'));
        if (!addr) return '—';
        const city = addr.city || addr.City;
        const state = addr.state || addr.State;
        return [city, state].filter(Boolean).join(', ') || '—';
      } },
    { key: 'active', label: 'Active', render: r => {
        const v = get(r, 'isActive', 'active', 'employeeActive');
        return v === undefined ? '—' : fmtYesNo(v);
      } },
  ];

  return (
    <div>
      <Toolbar search={search} onSearch={onSearch} onReload={onReload} loading={loading} endpointPath={endpointPath}
        rowCount={rows.length} total={total} placeholder="Search name, email, id…" />
      {error && <ErrorBanner error={error} endpointPath={endpointPath}
        hint="Open the Diagnostics tab and probe other candidates if 404." />}
      {!error && !loading && (
        <DataTable rows={rows} columns={columns} emptyLabel="employees" expandable
          rowKey={r => get(r, 'employeeId', 'id') ?? Math.random()} />
      )}
    </div>
  );
}

function JobCodesTab({ rows, total, loading, error, search, onSearch, onReload, endpointPath }) {
  const columns = [
    { key: 'code', label: 'Code', render: r => get(r, 'jobCodeID', 'jobcodeid', 'jobCodeId', 'code') ?? '—',
      tdStyle: { fontFamily: '"SF Mono", Menlo, Consolas, monospace', fontSize: 12, fontWeight: 600 } },
    { key: 'description', label: 'Description', render: r => get(r, 'description', 'name') || '—' },
    { key: 'active', label: 'Active', render: r => fmtYesNo(get(r, 'isActive', 'active')) },
    { key: 'clockable', label: 'Clockable', render: r => fmtYesNo(get(r, 'clockable', 'isClockable')) },
    { key: 'ot_counts', label: 'Counts to OT', render: r => fmtYesNo(get(r, 'countsTowardOvertime', 'countstowardovertime')) },
    { key: 'ot_earns', label: 'Earns OT', render: r => fmtYesNo(get(r, 'earnsOvertime', 'earnsovertime')) },
    { key: 'rate', label: 'Rate', render: r => {
        const v = get(r, 'defaultRate', 'defaultrate', 'rate');
        return v && v !== '0' && v !== 0 ? `$${Number(v).toFixed(2)}` : '—';
      } },
  ];

  return (
    <div>
      <Toolbar search={search} onSearch={onSearch} onReload={onReload} loading={loading} endpointPath={endpointPath}
        rowCount={rows.length} total={total} placeholder="Search code, description…" />
      {error && <ErrorBanner error={error} endpointPath={endpointPath} hint="Try /v1/jobcode or /v1/jobCodes in Diagnostics if 404." />}
      {!error && !loading && (
        <DataTable rows={rows} columns={columns} emptyLabel="job codes" expandable
          rowKey={r => get(r, 'jobCodeID', 'jobcodeid', 'id') ?? Math.random()} />
      )}
    </div>
  );
}

function HoursTab({ rows, loading, error, startDate, endDate, onStart, onEnd, onFetch, fetchedAt, endpointPath, employeeById, jobCodeById }) {
  const [showEmpty, setShowEmpty] = useState(false);

  const employeeName = useCallback((r) => {
    const emp = r._employee || r.employee;
    const id = (emp && get(emp, 'employeeId', 'id')) ?? get(r, 'employeeId', 'employeeID', 'employee_id');
    if (id === undefined) return '—';
    const fullFromEmp = emp && get(emp, 'employeeName');
    if (fullFromEmp) return `${fullFromEmp} · #${id}`;
    const e = employeeById.get(String(id));
    if (e) {
      const first = get(e, 'firstName', 'first_name');
      const last = get(e, 'lastName', 'last_name');
      const full = [first, last].filter(Boolean).join(' ');
      if (full) return `${full} · #${id}`;
    }
    return `#${id}`;
  }, [employeeById]);

  const summary = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    const empsWithHours = new Set();
    const empsWithoutHours = new Set();
    const uniqueJobs = new Set();
    let totalMinutes = 0;
    rows.forEach(r => {
      const emp = r._employee || r.employee;
      const empId = (emp && get(emp, 'employeeId', 'id')) ?? get(r, 'employeeId', 'employeeID', 'employee_id');
      if (r._empty) {
        if (empId !== undefined) empsWithoutHours.add(String(empId));
        return;
      }
      const jobId = get(r.jobCode || {}, 'jobCodeId', 'jobCodeID', 'id')
                 ?? get(r, 'jobCodeId', 'jobCodeID', 'jobcodeid', 'job_code_id');
      if (empId !== undefined) empsWithHours.add(String(empId));
      if (jobId !== undefined) uniqueJobs.add(String(jobId));
      totalMinutes += entryHours(r) * 60;
    });
    return {
      totalHours: totalMinutes / 60,
      employeesWithHours: empsWithHours.size,
      employeesWithoutHours: empsWithoutHours.size,
      jobs: uniqueJobs.size,
      dataRows: rows.length - rows.filter(r => r._empty).length,
    };
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (showEmpty) return rows;
    return rows.filter(r => !r._empty);
  }, [rows, showEmpty]);

  const columns = [
    { key: 'date', label: 'Date', render: r => fmtDate(get(r, 'timeIn', 'date', 'punchDate', 'clockInTime', 'inTime', 'startTime', 'startDate')) },
    { key: 'employee', label: 'Employee', render: r => employeeName(r) },
    { key: 'job', label: 'Job Code', render: r => {
        if (r._empty) return <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>{r._info}</span>;
        const jc = r.jobCode || {};
        const id = get(jc, 'jobCodeId', 'jobCodeID', 'id')
                ?? get(r, 'jobCodeId', 'jobCodeID', 'jobcodeid', 'job_code_id');
        if (id === undefined) return '—';
        const j = jobCodeById.get(String(id));
        const desc = j && get(j, 'description', 'name');
        return desc ? `${id} · ${desc}` : `#${id}`;
      } },
    { key: 'in', label: 'Clock In', render: r => fmtTime(get(r, 'timeIn', 'clockInTime', 'inTime', 'startTime')) },
    { key: 'out', label: 'Clock Out', render: r => fmtTime(get(r, 'timeOut', 'clockOutTime', 'outTime', 'endTime')) },
    { key: 'hours', label: 'Hours', render: r => {
        if (r._empty) return '—';
        const total = entryHours(r);
        return total > 0 ? total.toFixed(2) : '—';
      }, tdStyle: { textAlign: 'right', fontFamily: '"SF Mono", Menlo, Consolas, monospace', fontWeight: 600 } },
    { key: 'ot', label: 'OT', render: r => {
        const h = r && r.hours;
        if (!h || typeof h !== 'object') return '—';
        const ot = parseHMM(h.overtime1) + parseHMM(h.overtime2);
        return ot > 0 ? ot.toFixed(2) : '—';
      }, tdStyle: { textAlign: 'right', fontFamily: '"SF Mono", Menlo, Consolas, monospace', color: 'var(--orange)', fontWeight: 600 } },
    { key: 'rate', label: 'Rate', render: r => {
        const rate = get(r, 'rate');
        if (!rate) return '—';
        const n = Number(rate);
        return isNaN(n) ? String(rate) : `$${n.toFixed(2)}`;
      }, tdStyle: { textAlign: 'right', fontFamily: '"SF Mono", Menlo, Consolas, monospace', color: 'var(--gray-600)' } },
  ];

  const setRange = (startD, endD) => { onStart(isoDate(startD)); onEnd(isoDate(endD)); };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12.5, color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: 6 }}>
          Start <input type="date" value={startDate} onChange={e => onStart(e.target.value)} style={{ ...INPUT, padding: '5px 8px' }} />
        </label>
        <label style={{ fontSize: 12.5, color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: 6 }}>
          End <input type="date" value={endDate} onChange={e => onEnd(e.target.value)} style={{ ...INPUT, padding: '5px 8px' }} />
        </label>
        <div style={{ display: 'flex', gap: 4 }}>
          {[
            { label: 'Today', fn: () => setRange(new Date(), new Date()) },
            { label: 'Last 7', fn: () => setRange(daysAgo(6), new Date()) },
            { label: 'Last 14', fn: () => setRange(daysAgo(13), new Date()) },
            { label: 'This month', fn: () => { const s = new Date(); s.setDate(1); setRange(s, new Date()); } },
          ].map(p => (
            <button key={p.label} onClick={p.fn} style={{
              padding: '5px 10px', fontSize: 11, border: '1px solid var(--gray-300)', borderRadius: 6,
              background: '#fff', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--gray-700)',
            }}>{p.label}</button>
          ))}
        </div>
        <button onClick={onFetch} disabled={loading} style={BTN('var(--orange)', loading)}>
          <Calendar size={13} /> {loading ? 'Fetching…' : 'Fetch hours'}
        </button>
        <div style={{ flex: 1 }} />
        <code style={{ fontSize: 11, color: 'var(--gray-400)', fontFamily: '"SF Mono", Menlo, Consolas, monospace' }}>{endpointPath}</code>
      </div>

      {summary && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <SummaryCard label="Total hours" value={summary.totalHours.toFixed(2)} />
          <SummaryCard label="With hours" value={summary.employeesWithHours} />
          <SummaryCard label="No hours" value={summary.employeesWithoutHours} />
          <SummaryCard label="Job codes used" value={summary.jobs} />
          <SummaryCard label="Data rows" value={summary.dataRows} />
          {fetchedAt && <SummaryCard label="Fetched" value={fetchedAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} />}
          {summary.employeesWithoutHours > 0 && (
            <label style={{ fontSize: 12, color: 'var(--gray-600)', display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
              <input type="checkbox" checked={showEmpty} onChange={e => setShowEmpty(e.target.checked)} />
              Show {summary.employeesWithoutHours} no-hours employees
            </label>
          )}
        </div>
      )}

      {error && <ErrorBanner error={error} endpointPath={endpointPath}
        hint="Hours endpoint not confirmed — open Diagnostics and probe /v1/hours, /v1/individualhours, /v1/punches, /v1/timesheets, then Save as Hours." />}

      {!error && !loading && rows.length === 0 && !fetchedAt && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', border: '1px dashed var(--gray-300)', borderRadius: 8 }}>
          Pick a date range and click <strong>Fetch hours</strong>.
        </div>
      )}
      {!error && !loading && rows.length === 0 && fetchedAt && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', border: '1px dashed var(--gray-300)', borderRadius: 8 }}>
          No hours in {startDate} → {endDate}.
        </div>
      )}
      {!error && rows.length > 0 && (
        <DataTable rows={visibleRows} columns={columns} emptyLabel="hours" expandable
          rowKey={(r, i) => get(r, 'id', 'hoursId') ?? `${r._employee?.employeeId}-${i}`} />
      )}
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div style={{ padding: '10px 16px', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, minWidth: 120 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function DiagnosticsTab({ onEndpointSaved }) {
  const [auth, setAuth] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [probePath, setProbePath] = useState('/v1/hours');
  const [probeMethod, setProbeMethod] = useState('GET');
  const [probeQuery, setProbeQuery] = useState(`startDate=${isoDate(daysAgo(7))}&stopDate=${isoDate(new Date())}`);
  const [probeResult, setProbeResult] = useState(null);
  const [probeLoading, setProbeLoading] = useState(false);

  const checkAuth = useCallback(async () => {
    setAuthLoading(true);
    try { setAuth(await tcpCall({ action: 'auth' })); }
    finally { setAuthLoading(false); }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const runProbe = async () => {
    setProbeLoading(true);
    try {
      const query = {};
      (probeQuery || '').split('&').forEach(kv => {
        const [k, v] = kv.split('=');
        if (k && v !== undefined) query[k.trim()] = decodeURIComponent(v);
      });
      setProbeResult(await tcpCall({ path: probePath, method: probeMethod, query }));
    } finally { setProbeLoading(false); }
  };

  const saveAs = (key, label) => {
    localStorage.setItem(key, probePath);
    onEndpointSaved?.();
    alert(`Saved "${probePath}" as the new default for ${label}.`);
  };

  const candidates = [
    '/v1/employees', '/v1/jobcodes',
    '/v1/hours', '/v1/individualhours', '/v1/punches', '/v1/timesheets',
    '/v1/positions', '/v1/classifications', '/v1/companies',
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <section style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>OAuth Token</h3>
          <button onClick={checkAuth} disabled={authLoading} style={BTN('var(--navy)', authLoading)}>
            <RefreshCw size={13} /> {authLoading ? 'Checking…' : 'Re-check'}
          </button>
        </div>
        {auth && <pre style={CODE}>{JSON.stringify(auth, null, 2)}</pre>}
      </section>

      <section style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Probe an endpoint</h3>
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 12 }}>
          Calls <code>api.tcplusondemand.com{'{path}?query}'}</code> via the proxy. Use this to find the right URL, then "Save as" so the main tab uses it.
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <select value={probeMethod} onChange={e => setProbeMethod(e.target.value)} style={{ ...INPUT, width: 90 }}>
            <option>GET</option><option>POST</option><option>PUT</option><option>DELETE</option>
          </select>
          <input value={probePath} onChange={e => setProbePath(e.target.value)} style={{ ...INPUT, flex: 2, minWidth: 200 }} placeholder="/v1/employees" />
          <input value={probeQuery} onChange={e => setProbeQuery(e.target.value)} style={{ ...INPUT, flex: 2, minWidth: 200 }} placeholder="startDate=2026-04-15&stopDate=2026-04-22" />
          <button onClick={runProbe} disabled={probeLoading} style={BTN('var(--orange)', probeLoading)}>
            {probeLoading ? 'Probing…' : 'Probe'}
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {candidates.map(c => (
            <button key={c} onClick={() => setProbePath(c)} style={{
              padding: '3px 10px', fontSize: 11, border: '1px solid var(--gray-300)', borderRadius: 12,
              background: probePath === c ? 'var(--gray-100)' : '#fff', cursor: 'pointer', fontFamily: 'inherit',
              color: 'var(--gray-700)',
            }}>{c}</button>
          ))}
        </div>

        {probeResult && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 12, flexWrap: 'wrap' }}>
              <span style={{ padding: '2px 8px', borderRadius: 10,
                background: probeResult.ok ? '#dcfce7' : '#fee2e2',
                color: probeResult.ok ? '#166534' : '#991b1b', fontWeight: 600 }}>
                HTTP {probeResult.status || probeResult.httpStatus}
              </span>
              <code style={{ fontSize: 11, color: 'var(--gray-600)' }}>{probeResult.url || probePath}</code>
              <div style={{ flex: 1 }} />
              {probeResult.ok && (
                <>
                  <button onClick={() => saveAs(LS_EMP, 'Employees')} style={BTN('var(--green)')}>Save as Employees</button>
                  <button onClick={() => saveAs(LS_JOB, 'Job Codes')} style={BTN('var(--green)')}>Save as Job Codes</button>
                  <button onClick={() => saveAs(LS_HOURS, 'Hours')} style={BTN('var(--green)')}>Save as Hours</button>
                </>
              )}
            </div>
            <pre style={CODE}>{JSON.stringify(probeResult.data ?? probeResult, null, 2).slice(0, 8000)}</pre>
          </>
        )}
      </section>

      <section style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Current overrides</h3>
        <div style={{ fontSize: 12.5, color: 'var(--gray-700)', display: 'grid', gap: 4 }}>
          {[
            [LS_EMP, 'Employees', DEFAULT_EMPLOYEES_PATH],
            [LS_JOB, 'Job Codes', DEFAULT_JOBCODES_PATH],
            [LS_HOURS, 'Hours', DEFAULT_HOURS_PATH],
          ].map(([key, label, def]) => {
            const override = localStorage.getItem(key);
            return (
              <div key={key}>
                <strong>{label}:</strong> <code>{override || def}</code>{' '}
                {override && <button onClick={() => { localStorage.removeItem(key); onEndpointSaved?.(); }} style={{ marginLeft: 8, fontSize: 11, cursor: 'pointer' }}>Reset</button>}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
